import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import authAtom, { type User } from '../authAtom';
import { publicClient } from '../api/clients';
import buildHttpError from '../../../shared/errors/HttpError';

interface LoginResponse {
  user: User;
  accessToken: string;
}
const isLoginResponse = (v: unknown): v is LoginResponse => {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  const user = o.user as Record<string, unknown> | undefined;
  return (
    typeof o.accessToken === 'string' &&
    user !== undefined &&
    typeof user.id === 'number' &&
    typeof user.name === 'string' &&
    typeof user.email === 'string' &&
    typeof user.isActive === 'boolean'
  );
};

const useAuth = () => {
  const [auth, setAuth] = useAtom(authAtom);
  const navigate = useNavigate();
  const login = async (idToken: string) => {
    const response = await publicClient.auth.login.$post({ json: { idToken } });
    if (!response.ok) throw await buildHttpError(response);
    const raw = await response.json();
    if (!isLoginResponse(raw)) throw new Error('Invalid login response');
    setAuth({ user: raw.user, accessToken: raw.accessToken });
    void navigate('/history');
  };

  const logout = async () => {
    try {
      await publicClient.auth.logout.$post();
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setAuth(null);
      void navigate('/login');
    }
  };

  return {
    user: auth?.user ?? null,
    isAuthenticated: !!(auth?.accessToken ?? null),
    login,
    logout,
  };
};

export default useAuth;
